import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { contractsAPI, usersAPI, serviceTypesAPI, vehicleTypesAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import RoutePriceCalculator from "../components/RoutePriceCalculator";
import ClientSelector from "../components/ClientSelector";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  User,
  Receipt,
  Plus,
  AlertCircle,
  X,
  ChevronDown,
  ChevronRight,
  Info,
  Undo2
} from "lucide-react";
import { cn } from "../lib/utils";

// Build version for deployment verification
const COMPONENT_VERSION = "4.9.0"; // Fixed price decimal step

const VAT_RATE = 0.16;

const ON_BEHALF_REASONS = [
  { value: "NO_ACCESS", label: "Vendedor sin acceso al sistema" },
  { value: "OPERATIONAL_SUPPORT", label: "Apoyo operativo" },
  { value: "URGENT_CAPTURE", label: "Captura urgente" },
  { value: "SHIFT_COVERAGE", label: "Cobertura de turno" },
  { value: "OTHER", label: "Otro" }
];

export default function NuevoContrato() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [vendedores, setVendedores] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  // v4.61.0 (Paso 2): pop-up de advertencia de disponibilidad exacta
  const [availabilityDialog, setAvailabilityDialog] = useState({ open: false, info: null });
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [formData, setFormData] = useState({
    // CRM Client (v4.12.0)
    client_id: null,
    // Client info (legacy)
    client_name: "",
    client_phone: "",
    client_email: "",
    // Route info
    origin: "",
    destination: "",
    origin_lat: null,
    origin_lng: null,
    dest_lat: null,
    dest_lng: null,
    pickup_datetime: "",
    end_datetime: "",  // P0-2: End date/time required
    service_type: "transfer",
    transfer_trip_type: "SENCILLO", // For TRANSFER: SENCILLO | REDONDO
    // Optional fields - marked clearly as optional
    passengers: "",  // Optional - can be empty
    luggage: "",     // Optional - can be empty
    flight_number: "",
    full_itinerary: "",  // NEW: Full travel itinerary
    special_requirements: "",
    // Vehicle lines - REQUIRED - NEW format with individual prices
    // [{id: "uuid", type: "sedan", capacity: 4, base_price: 1500}, ...]
    vehicle_lines: [],
    // Pricing - calculated from vehicle_lines
    base_price: 0,
    extras: [],
    requires_invoice: false,
    vat_rate: VAT_RATE,
    // Multi-currency
    currency: "MXN",
    exchange_rate: null,
    // Route/pricing metadata
    distance_km: null,
    duration_min: null,
    suggested_price: null,
    pricing_method: null,
    pricing_rules_snapshot: null,
    route_source: null,
    // Sales owner
    sales_owner_id: "",
    on_behalf_reason: "",
    on_behalf_note: "",
    vl_reassign_reason: "",
    // Notes
    notes: ""
  });
  const [newExtra, setNewExtra] = useState({ name: "", amount: 0 });
  const [newVehicleLine, setNewVehicleLine] = useState({ type: "", quantity: 1, capacity: 4, base_price: 0 });
  // v4.59.0: Intención operativa por línea — UI colapsable
  const [expandedLineIds, setExpandedLineIds] = useState(new Set());
  const toggleLineExpand = (id) => {
    setExpandedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const updateLineIntentField = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.map(v =>
        v.id === id ? { ...v, [field]: value } : v
      )
    }));
  };
  const inheritGlobalForLine = (id) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.map(v =>
        v.id === id ? {
          ...v,
          line_start_datetime: null,
          line_end_datetime: null,
          line_origin: null,
          line_destination: null,
          line_notes: null,
        } : v
      )
    }));
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Helper to check user role capabilities for salesperson assignment (P0)
  const isVendedorOnly = user?.role === "vendedor";
  const isVendedorLogistica = user?.role === "vendedor_logistica";
  const isAdminRole = user?.role === "admin";
  const isAdminVendedor = user?.role === "admin_vendedor";
  const isLogisticaOnly = user?.role === "logistica";
  
  // admin_vendedor has admin permissions but behaves like admin for UI
  const hasAdminPermissions = isAdminRole || isAdminVendedor;
  
  // Solo vendedor puro se auto-asigna por DEFECTO, pero ahora también puede cambiar de vendedor
  // vendedor_logistica, admin, logistica seleccionan manualmente
  const isAutoAssignRole = isVendedorOnly;
  
  // Determine if vendor selection is required (mandatory for non-vendedor roles)
  const vendorSelectionRequired = hasAdminPermissions || isLogisticaOnly || isVendedorLogistica;
  
  // These roles can change the vendor selection
  // v4.60.1: vendedor puro TAMBIÉN puede cambiar el vendedor (crear a nombre de otro con justificación)
  const canChangeVendor = hasAdminPermissions || isLogisticaOnly || isVendedorLogistica || isVendedorOnly;

  useEffect(() => {
    if (user && !formData.sales_owner_id) {
      // Auto-assign only for vendedor puro
      // admin_vendedor and vendedor_logistica do NOT auto-assign
      if (isAutoAssignRole) {
        setFormData(prev => ({ ...prev, sales_owner_id: user.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAutoAssignRole]);

  // P1-C DEPRECATED: sales_agent_id auto-resolve removed
  // sales_owner_id is now the single source of truth for commissions

  // Set default vehicle type when types are loaded
  useEffect(() => {
    if (vehicleTypes.length > 0 && !newVehicleLine.type) {
      const defaultType = vehicleTypes[0];
      setNewVehicleLine(prev => ({
        ...prev,
        type: defaultType.code,
        capacity: defaultType.capacity || 4
      }));
    }
  }, [vehicleTypes]);

  const fetchInitialData = async () => {
    try {
      const [vendedoresRes, serviceTypesRes, vehicleTypesRes] = await Promise.all([
        usersAPI.getVendedores(),
        serviceTypesAPI.getAll(),
        vehicleTypesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setVendedores(vendedoresRes.data);
      setServiceTypes(serviceTypesRes.data);
      
      // Filter only active vehicle types
      const activeVehicleTypes = (vehicleTypesRes.data || []).filter(t => t.is_active !== false);
      setVehicleTypes(activeVehicleTypes.length > 0 ? activeVehicleTypes : [
        { code: "sedan", name: "Sedán", capacity: 4 },
        { code: "suv", name: "SUV", capacity: 6 },
        { code: "van", name: "Van", capacity: 14 },
        { code: "sprinter", name: "Sprinter", capacity: 20 }
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const isOnBehalf = formData.sales_owner_id && user && formData.sales_owner_id !== user.id;
  
  // Check if vendedor_logistica is reassigning to another vendor (requires reason)
  const isVLReassigning = isVendedorLogistica && isOnBehalf;

  // Handle price suggestion from RoutePriceCalculator
  const handleRoutePriceApplied = (suggestion) => {
    // Apply distance multiplier for TRANSFER REDONDO
    let multiplier = 1;
    if (formData.service_type === "transfer" && formData.transfer_trip_type === "REDONDO") {
      multiplier = 2;
    }
    
    const effectivePrice = suggestion.basePrice * multiplier;
    
    setFormData(prev => ({
      ...prev,
      base_price: effectivePrice,
      suggested_price: suggestion.suggestedPrice,
      pricing_method: suggestion.pricingMethod,
      pricing_rules_snapshot: suggestion.rulesSnapshot,
      route_source: suggestion.routeSource,
      distance_km: (suggestion.distanceKm || 0) * multiplier || prev.distance_km,
      duration_min: (suggestion.durationMin || 0) * multiplier || prev.duration_min
    }));
  };

  // Handle adding vehicle line - NEW: Expand quantity to individual entries with price
  const handleAddVehicleLine = () => {
    if (newVehicleLine.quantity > 0 && newVehicleLine.type) {
      // Expand quantity to individual entries
      const newLines = [];
      const basePrice = newVehicleLine.base_price || 0;
      
      for (let i = 0; i < newVehicleLine.quantity; i++) {
        newLines.push({
          id: `${Date.now()}-${i}`,
          type: newVehicleLine.type,
          capacity: newVehicleLine.capacity,
          base_price: basePrice,
          notes: "",
          // v4.59.0: campos opcionales (null = hereda del contrato global)
          line_start_datetime: null,
          line_end_datetime: null,
          line_origin: null,
          line_destination: null,
          line_notes: null,
        });
      }
      
      setFormData(prev => ({
        ...prev,
        vehicle_lines: [...prev.vehicle_lines, ...newLines]
      }));
      
      const defaultType = vehicleTypes[0] || { code: "sedan", capacity: 4 };
      setNewVehicleLine({ type: defaultType.code, quantity: 1, capacity: defaultType.capacity || 4, base_price: 0 });
    }
  };
  
  // Helper: Get vehicle type label - flags ambiguous legacy codes for review
  const getVehicleTypeLabel = (typeCode) => {
    if (!typeCode) return "Sin tipo";
    const exactMatch = vehicleTypes.find(vt => vt.code === typeCode);
    if (exactMatch) return exactMatch.name;
    
    const possibleMatches = vehicleTypes.filter(vt => 
      vt.code.startsWith(typeCode + "_") || vt.code.startsWith(typeCode.toLowerCase() + "_")
    );
    if (possibleMatches.length > 0) {
      const baseName = typeCode.charAt(0).toUpperCase() + typeCode.slice(1).toLowerCase();
      return `${baseName} (revisar capacidad)`;
    }
    
    const fallbackLabels = {
      sedan: "Sedán", suv: "SUV", van: "Van", sprinter: "Sprinter",
      suburban: "Suburban", autobus: "Autobús (revisar capacidad)", 
      bus: "Autobús (revisar capacidad)", limusina: "Limusina"
    };
    return fallbackLabels[typeCode.toLowerCase()] || typeCode;
  };

  const handleRemoveVehicleLine = (id) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.filter(v => v.id !== id)
    }));
  };
  
  // NEW: Update individual vehicle line price
  const handleUpdateVehicleLinePrice = (id, newPrice) => {
    setFormData(prev => ({
      ...prev,
      vehicle_lines: prev.vehicle_lines.map(v => 
        v.id === id ? { ...v, base_price: parseFloat(newPrice) || 0 } : v
      )
    }));
  };
  
  // NEW: Calculate totals from vehicle lines
  const calculateTotalsFromLines = () => {
    const vehicleLinesTotal = formData.vehicle_lines.reduce((sum, v) => sum + (v.base_price || 0), 0);
    const extrasTotal = formData.extras.reduce((sum, e) => sum + e.amount, 0);
    const subtotal = vehicleLinesTotal + extrasTotal;
    const vatAmount = formData.requires_invoice ? subtotal * formData.vat_rate : 0;
    const total = subtotal + vatAmount;
    
    return { vehicleLinesTotal, extrasTotal, subtotal, vatAmount, total };
  };
  
  // Auto-update base_price when vehicle_lines change
  useEffect(() => {
    if (formData.vehicle_lines.length > 0) {
      const totals = calculateTotalsFromLines();
      setFormData(prev => ({
        ...prev,
        base_price: totals.vehicleLinesTotal
      }));
    }
  }, [formData.vehicle_lines]);

  const handleAddExtra = () => {
    if (newExtra.name && newExtra.amount > 0) {
      setFormData(prev => ({
        ...prev,
        extras: [...prev.extras, { ...newExtra }]
      }));
      setNewExtra({ name: "", amount: 0 });
    }
  };

  const handleRemoveExtra = (index) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations - require CRM client or legacy name+phone
    if (!formData.client_id && (!formData.client_name || !formData.client_phone)) {
      toast.error("Debe seleccionar un cliente");
      return;
    }
    if (!formData.origin || !formData.destination || !formData.pickup_datetime) {
      toast.error("Complete origen, destino y fecha de inicio");
      return;
    }
    // P0-2: Validate end_datetime
    if (!formData.end_datetime) {
      toast.error("Debe especificar fecha y hora de finalización");
      return;
    }
    if (new Date(formData.end_datetime) <= new Date(formData.pickup_datetime)) {
      toast.error("La fecha y hora de finalización debe ser posterior a la fecha y hora de inicio");
      return;
    }
    // Vehicle lines required
    if (!formData.vehicle_lines || formData.vehicle_lines.length === 0) {
      toast.error("Debe seleccionar al menos un tipo de unidad");
      return;
    }
    // VALIDATION: Vendor is required for admin and logistica-only users
    if (vendorSelectionRequired && !formData.sales_owner_id) {
      toast.error("Debe seleccionar un vendedor asignado");
      return;
    }
    // sales_agent_id validation removed - sales_owner_id is the single source
    if (isOnBehalf && !formData.on_behalf_reason) {
      toast.error("Debe seleccionar una razón al capturar para otro vendedor");
      return;
    }
    // Validate vl_reassign_reason if vendedor_logistica is reassigning
    if (isVLReassigning && !formData.vl_reassign_reason) {
      toast.error("Debe especificar el motivo de reasignación del vendedor");
      return;
    }
    if (formData.currency === "USD" && (!formData.exchange_rate || formData.exchange_rate <= 0)) {
      toast.error("Debe especificar un tipo de cambio válido para contratos en USD");
      return;
    }
    if (formData.base_price <= 0) {
      toast.error("El precio base debe ser mayor a 0. Agregue precios a las unidades.");
      return;
    }

    // v4.61.0 (Paso 2): pre-chequeo de disponibilidad EXACTA (mismo tipo + capacidad) antes de crear
    setCheckingAvailability(true);
    try {
      const pre = await contractsAPI.availabilityPrecheck({
        pickup_datetime: formData.pickup_datetime,
        end_datetime: formData.end_datetime,
        vehicle_lines: formData.vehicle_lines.map(v => ({ type: v.type, capacity: v.capacity })),
      });
      if (pre.data && pre.data.sufficient === false) {
        setCheckingAvailability(false);
        setAvailabilityDialog({ open: true, info: pre.data });
        return; // esperar decisión del vendedor (Cancelar / Continuar)
      }
    } catch (e) {
      // Si el pre-check falla, no bloquear la creación del contrato
    }
    setCheckingAvailability(false);
    await doSubmit(false);
  };

  // Vendedor confirmó continuar sin disponibilidad interna exacta suficiente
  const handleAvailabilityContinue = async () => {
    setAvailabilityDialog({ open: false, info: null });
    await doSubmit(true);
  };

  const doSubmit = async (overrideConfirmed) => {
    setLoading(true);
    try {
      // Calculate totals from vehicle lines
      const totals = calculateTotalsFromLines();
      const extrasTotal = totals.extrasTotal;
      const subtotal = totals.subtotal;
      const vatAmount = formData.requires_invoice ? subtotal * VAT_RATE : 0;
      const total = subtotal + vatAmount;

      // MXN equivalents
      const exchangeRate = formData.exchange_rate || 1;
      const subtotalMXN = formData.currency === "USD" ? subtotal * exchangeRate : subtotal;
      const vatMXN = formData.currency === "USD" ? vatAmount * exchangeRate : vatAmount;
      const totalMXN = formData.currency === "USD" ? total * exchangeRate : total;

      const contractData = {
        // No quote_id - direct contract
        quote_id: null,
        // CRM Client (v4.12.0)
        client_id: formData.client_id,
        // Client (legacy fields)
        client_name: formData.client_name,
        client_phone: formData.client_phone,
        client_email: formData.client_email,
        // Route
        origin: formData.origin,
        destination: formData.destination,
        origin_lat: formData.origin_lat,
        origin_lng: formData.origin_lng,
        dest_lat: formData.dest_lat,
        dest_lng: formData.dest_lng,
        pickup_datetime: formData.pickup_datetime,
        end_datetime: formData.end_datetime,  // P0-2: Required
        service_type: formData.service_type,
        transfer_trip_type: formData.transfer_trip_type || "SENCILLO",
        // Optional fields - send only if provided
        passengers: formData.passengers ? parseInt(formData.passengers) : null,
        luggage: formData.luggage ? parseInt(formData.luggage) : null,
        flight_number: formData.flight_number || null,
        full_itinerary: formData.full_itinerary || null,  // NEW field
        special_requirements: formData.special_requirements || null,
        // Vehicle lines - NEW format with individual prices + v4.59.0 line_*
        vehicle_lines: formData.vehicle_lines.map(v => ({
          id: v.id,
          type: v.type,
          capacity: v.capacity,
          base_price: v.base_price || 0,
          notes: v.notes || "",
          // v4.59.0: campos opcionales de intención operativa por línea
          line_start_datetime: v.line_start_datetime || null,
          line_end_datetime: v.line_end_datetime || null,
          line_origin: v.line_origin || null,
          line_destination: v.line_destination || null,
          line_notes: v.line_notes || null,
        })),
        // Pricing - calculated from vehicle lines
        base_price: totals.vehicleLinesTotal,
        extras: formData.extras,
        total_price: total,
        requires_invoice: formData.requires_invoice,
        vat_rate: formData.requires_invoice ? VAT_RATE : 0,
        subtotal_amount: subtotal,
        vat_amount: vatAmount,
        // Multi-currency
        currency: formData.currency,
        exchange_rate: formData.currency === "USD" ? formData.exchange_rate : null,
        subtotal_amount_mxn: subtotalMXN,
        vat_amount_mxn: vatMXN,
        total_amount_mxn: totalMXN,
        // Route metadata
        distance_km: formData.distance_km,
        duration_min: formData.duration_min,
        suggested_price: formData.suggested_price,
        pricing_method: formData.pricing_method,
        pricing_rules_snapshot: formData.pricing_rules_snapshot,
        route_source: formData.route_source,
        // Sales owner
        sales_owner_id: formData.sales_owner_id,
        on_behalf_reason: isOnBehalf ? formData.on_behalf_reason : null,
        on_behalf_note: isOnBehalf ? formData.on_behalf_note : null,
        // FIX P0: vendedor_logistica reasignando a otro vendedor requiere este motivo (auditoría)
        vl_reassign_reason: isVLReassigning ? formData.vl_reassign_reason : null,
        // Notes
        notes: formData.notes,
        // v4.61.0 (Paso 2): el vendedor confirmó continuar sin disponibilidad interna exacta
        availability_override_confirmed: overrideConfirmed,
      };

      const response = await contractsAPI.create(contractData);
      toast.success("Contrato creado exitosamente");
      navigate(`/contratos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear contrato");
    } finally {
      setLoading(false);
    }
  };

  // Calculate pricing preview
  const subtotalPrice = formData.base_price + formData.extras.reduce((sum, e) => sum + e.amount, 0);
  const vatAmount = formData.requires_invoice ? subtotalPrice * VAT_RATE : 0;
  const totalPrice = subtotalPrice + vatAmount;
  const exchangeRate = formData.exchange_rate || 1;
  const totalMXN = formData.currency === "USD" ? totalPrice * exchangeRate : totalPrice;

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="nuevo-contrato-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Nuevo Contrato Directo</h1>
            <p className="text-slate-500 mt-1">Crear contrato sin cotización previa</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
          {/* Client Info */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg text-slate-800">Datos del Cliente</h3>
              
              {/* CRM Client Selector */}
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <ClientSelector
                  value={formData.client_id}
                  onChange={(clientId, clientData) => {
                    if (clientData) {
                      setFormData(prev => ({
                        ...prev,
                        client_id: clientId,
                        client_name: clientData.name || clientData.legal_name,
                        client_phone: clientData.phone || "",
                        client_email: clientData.email || ""
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        client_id: null,
                        client_name: "",
                        client_phone: "",
                        client_email: ""
                      }));
                    }
                  }}
                  required={true}
                  placeholder="Buscar o crear cliente..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Route Info */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg text-slate-800">Información del Servicio</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Origen *</Label>
                  <Input
                    value={formData.origin}
                    onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                    placeholder="Dirección de origen"
                    required
                    data-testid="origin-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Destino *</Label>
                  <Input
                    value={formData.destination}
                    onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                    placeholder="Dirección de destino"
                    required
                    data-testid="destination-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha y Hora de Inicio *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.pickup_datetime}
                    onChange={(e) => setFormData(prev => ({ ...prev, pickup_datetime: e.target.value }))}
                    required
                    data-testid="pickup-datetime-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha y Hora de Fin *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_datetime: e.target.value }))}
                    min={formData.pickup_datetime}
                    required
                    data-testid="end-datetime-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Servicio</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                  >
                    <SelectTrigger data-testid="service-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.length > 0 ? (
                        serviceTypes.map(st => (
                          <SelectItem key={st.code} value={st.code}>{st.name}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="transfer">Transfer</SelectItem>
                          <SelectItem value="aeropuerto">Aeropuerto</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="disposicion">Disposición</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Transfer Trip Type: SENCILLO | REDONDO */}
              {formData.service_type === "transfer" && (
                <div className="space-y-2">
                  <Label>Tipo de Transfer</Label>
                  <Select 
                    value={formData.transfer_trip_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, transfer_trip_type: value }))}
                  >
                    <SelectTrigger data-testid="transfer-trip-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SENCILLO">Sencillo (solo ida)</SelectItem>
                      <SelectItem value="REDONDO">Redondo (ida y vuelta)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.transfer_trip_type === "REDONDO" && (
                    <p className="text-xs text-blue-600">* El precio se duplicará automáticamente</p>
                  )}
                </div>
              )}
              
              {/* Optional fields section - clearly marked */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                <p className="text-sm text-slate-500 font-medium">Campos opcionales</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Pasajeros <span className="text-xs text-slate-400">(opcional)</span></Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Cantidad"
                      value={formData.passengers}
                      onChange={(e) => setFormData(prev => ({ ...prev, passengers: e.target.value }))}
                      data-testid="passengers-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Equipaje <span className="text-xs text-slate-400">(opcional)</span></Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="Piezas"
                      value={formData.luggage}
                      onChange={(e) => setFormData(prev => ({ ...prev, luggage: e.target.value }))}
                      data-testid="luggage-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Vuelo <span className="text-xs text-slate-400">(opcional)</span></Label>
                    <Input
                      value={formData.flight_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, flight_number: e.target.value }))}
                      placeholder="AA1234"
                      data-testid="flight-number-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Full Itinerary - NEW */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Itinerario de viaje completo</Label>
                <Textarea
                  value={formData.full_itinerary}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_itinerary: e.target.value }))}
                  placeholder="Describa el itinerario detallado del servicio...&#10;Ej: 10:00 - Recoger en aeropuerto&#10;10:30 - Traslado a hotel&#10;11:00 - Check-in"
                  rows={4}
                  className="font-mono text-sm"
                  data-testid="full-itinerary-input"
                />
                <p className="text-xs text-slate-500">Este itinerario aparecerá en el contrato y en las órdenes de servicio</p>
              </div>
              
              <div className="space-y-2">
                <Label>Requerimientos especiales</Label>
                <Textarea
                  value={formData.special_requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, special_requirements: e.target.value }))}
                  placeholder="Silla de bebé, wifi, etc."
                  rows={2}
                  data-testid="special-requirements-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Lines */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg text-slate-800">Tipo(s) de Unidad *</h3>
              </div>
              
              {/* Add vehicle line */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select 
                    value={newVehicleLine.type} 
                    onValueChange={(value) => {
                      const selectedType = vehicleTypes.find(t => t.code === value);
                      setNewVehicleLine(prev => ({ 
                        ...prev, 
                        type: value, 
                        capacity: selectedType?.capacity || 4 
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="vehicle-type-select">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map(vt => (
                        <SelectItem key={vt.code} value={vt.code}>
                          {vt.name} ({vt.capacity || 4} pax)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24 space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    Cant.
                    <span
                      title="Si Cant. > 1, se crearán líneas individuales que pueden personalizarse por separado (horario, origen, destino) después."
                      data-testid="quantity-tooltip"
                      className="cursor-help"
                    >
                      <Info className="w-3 h-3 text-slate-400" />
                    </span>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    className="w-20"
                    value={newVehicleLine.quantity}
                    onChange={(e) => setNewVehicleLine(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    data-testid="vehicle-quantity-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Precio c/u</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    className="w-28"
                    placeholder="0"
                    value={newVehicleLine.base_price || ""}
                    onChange={(e) => setNewVehicleLine(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    data-testid="vehicle-price-input"
                  />
                </div>
                <Button type="button" onClick={handleAddVehicleLine} size="sm" disabled={!newVehicleLine.type} className="mt-5">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Vehicle lines list - NEW: Individual entries with editable price + v4.59.0 line_* */}
              {formData.vehicle_lines.length > 0 && (
                <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                  <p className="text-xs text-slate-500 font-medium mb-2">
                    Unidades contratadas ({formData.vehicle_lines.length}):
                  </p>
                  {formData.vehicle_lines.map((v, i) => {
                    const typeName = getVehicleTypeLabel(v.type);
                    const isExpanded = expandedLineIds.has(v.id);
                    const hasCustomIntent = !!(v.line_start_datetime || v.line_end_datetime || v.line_origin || v.line_destination || v.line_notes);
                    return (
                      <div key={v.id || i} className="bg-white rounded border" data-testid={`vehicle-line-row-${i}`}>
                        <div className="flex items-center gap-2 p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleLineExpand(v.id)}
                            className="h-8 w-8 p-0"
                            data-testid={`toggle-line-intent-${i}`}
                            title="Mostrar/ocultar intención operativa de esta línea"
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </Button>
                          <span className="text-sm flex-1">
                            {i + 1}. {typeName} ({v.capacity} pax)
                            {hasCustomIntent && (
                              <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700">
                                Personalizada
                              </Badge>
                            )}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={v.base_price || 0}
                              onChange={(e) => handleUpdateVehicleLinePrice(v.id, e.target.value)}
                              className="w-24 h-8 text-sm"
                              data-testid={`line-base-price-${i}`}
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveVehicleLine(v.id)} className="h-8 w-8 p-0" data-testid={`remove-line-${i}`}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        {isExpanded && (
                          <div className="border-t bg-slate-50/70 p-3 space-y-3" data-testid={`line-intent-form-${i}`}>
                            <div className="flex items-start gap-2 text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded p-2">
                              <Info className="w-3.5 h-3.5 mt-0.5 text-blue-500 flex-shrink-0" />
                              <span>
                                Intención operativa de esta línea (opcional). Si dejas un campo vacío, hereda del contrato global.
                                Esta información alimenta el indicador de presión por tipo. PDFs, Órdenes de Servicio y Calendario siguen usando las fechas globales del contrato.
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Inicio operativo (línea)</Label>
                                <Input
                                  type="datetime-local"
                                  value={v.line_start_datetime || ""}
                                  onChange={(e) => updateLineIntentField(v.id, "line_start_datetime", e.target.value || null)}
                                  className="h-8 text-sm"
                                  data-testid={`line-start-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Fin operativo (línea)</Label>
                                <Input
                                  type="datetime-local"
                                  value={v.line_end_datetime || ""}
                                  onChange={(e) => updateLineIntentField(v.id, "line_end_datetime", e.target.value || null)}
                                  className="h-8 text-sm"
                                  data-testid={`line-end-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Origen específico (línea)</Label>
                                <Input
                                  type="text"
                                  value={v.line_origin || ""}
                                  onChange={(e) => updateLineIntentField(v.id, "line_origin", e.target.value || null)}
                                  placeholder={formData.origin ? `Hereda: ${formData.origin}` : "Hereda del contrato"}
                                  className="h-8 text-sm"
                                  data-testid={`line-origin-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Destino específico (línea)</Label>
                                <Input
                                  type="text"
                                  value={v.line_destination || ""}
                                  onChange={(e) => updateLineIntentField(v.id, "line_destination", e.target.value || null)}
                                  placeholder={formData.destination ? `Hereda: ${formData.destination}` : "Hereda del contrato"}
                                  className="h-8 text-sm"
                                  data-testid={`line-destination-${i}`}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Notas operativas (línea)</Label>
                              <Textarea
                                rows={2}
                                value={v.line_notes || ""}
                                onChange={(e) => updateLineIntentField(v.id, "line_notes", e.target.value || null)}
                                placeholder="Indicaciones específicas para esta unidad…"
                                className="text-sm"
                                data-testid={`line-notes-${i}`}
                              />
                            </div>
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => inheritGlobalForLine(v.id)}
                                className="h-7 text-xs"
                                data-testid={`inherit-line-${i}`}
                              >
                                <Undo2 className="w-3 h-3 mr-1" /> Heredar datos globales
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-2 border-t">
                    <span className="text-sm font-medium text-slate-700">
                      Total unidades: ${formData.vehicle_lines.reduce((sum, v) => sum + (v.base_price || 0), 0).toLocaleString("es-MX")} {formData.currency}
                    </span>
                  </div>
                </div>
              )}
              
              {formData.vehicle_lines.length === 0 && (
                <p className="text-xs text-amber-600">* Agregue al menos un tipo de unidad con su precio</p>
              )}
            </CardContent>
          </Card>

          {/* Route Price Calculator */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg text-slate-800">Calcular Precio</h3>
              <RoutePriceCalculator
                origin={formData.origin}
                destination={formData.destination}
                originLat={formData.origin_lat}
                originLng={formData.origin_lng}
                destLat={formData.dest_lat}
                destLng={formData.dest_lng}
                vehicleType={formData.vehicle_lines[0]?.type || "sedan"}
                passengers={formData.passengers}
                serviceType={formData.service_type}
                currency={formData.currency}
                exchangeRate={formData.exchange_rate}
                requiresInvoice={formData.requires_invoice}
                salesOwnerId={formData.sales_owner_id}
                onApplySuggestion={handleRoutePriceApplied}
              />
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg text-slate-800">Precio</h3>
              
              {/* Currency Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
                <div className="space-y-2">
                  <Label>Moneda</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      currency: value,
                      exchange_rate: value === "MXN" ? null : prev.exchange_rate
                    }))}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">🇲🇽 MXN - Peso Mexicano</SelectItem>
                      <SelectItem value="USD">🇺🇸 USD - Dólar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.currency === "USD" && (
                  <div className="space-y-2">
                    <Label>Tipo de Cambio *</Label>
                    <Input
                      type="number"
                      step="any"
                      min="1"
                      placeholder="Ej: 17.50"
                      value={formData.exchange_rate || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        exchange_rate: parseFloat(e.target.value) || null
                      }))}
                      required={formData.currency === "USD"}
                      data-testid="exchange-rate-input"
                    />
                    <p className="text-xs text-slate-500">1 USD = ? MXN</p>
                  </div>
                )}
              </div>

              {/* Base Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio Base *</Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.base_price || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    required
                    data-testid="base-price-input"
                    className={cn(
                      formData.suggested_price && formData.base_price !== formData.suggested_price
                        ? "border-amber-400"
                        : ""
                    )}
                  />
                  {formData.suggested_price && formData.base_price !== formData.suggested_price && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Diferente al sugerido (${formData.suggested_price.toLocaleString("es-MX")})
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Duración estimada de ruta (min)</Label>
                    <span 
                      className="text-xs text-slate-400 cursor-help" 
                      title="Estimación de Google Maps. El bloqueo real del vehículo depende de Fecha Inicio/Fin."
                    >
                      ⓘ
                    </span>
                  </div>
                  <Input
                    type="number"
                    value={formData.duration_min || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_min: parseInt(e.target.value) || null }))}
                    data-testid="duration-input"
                  />
                </div>
                {/* Tiempo reservado calculado */}
                {formData.pickup_datetime && formData.end_datetime && (
                  <div className="space-y-2">
                    <Label className="text-slate-500">Tiempo reservado</Label>
                    <div className="p-2 bg-slate-50 rounded border text-sm font-medium text-slate-700">
                      {(() => {
                        const start = new Date(formData.pickup_datetime);
                        const end = new Date(formData.end_datetime);
                        const diffMs = end - start;
                        if (diffMs <= 0) return "—";
                        const hours = Math.floor(diffMs / (1000 * 60 * 60));
                        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        if (hours > 0) return `${hours}h ${mins}min`;
                        return `${mins} min`;
                      })()}
                    </div>
                    <p className="text-xs text-slate-400">Periodo de bloqueo del vehículo</p>
                  </div>
                )}
              </div>

              {/* Extras */}
              <div className="space-y-4">
                <Label>Extras</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Concepto"
                    value={newExtra.name}
                    onChange={(e) => setNewExtra(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1"
                    data-testid="extra-name-input"
                  />
                  <Input
                    type="number"
                    placeholder="Monto"
                    value={newExtra.amount || ""}
                    onChange={(e) => setNewExtra(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="w-32"
                    data-testid="extra-amount-input"
                  />
                  <Button type="button" onClick={handleAddExtra} data-testid="add-extra-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.extras.length > 0 && (
                  <div className="space-y-2">
                    {formData.extras.map((extra, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span>{extra.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">${extra.amount.toLocaleString("es-MX")}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveExtra(index)}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoice Toggle */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  <div>
                    <Label className="font-medium text-blue-900">¿Requiere Factura?</Label>
                    <p className="text-sm text-blue-600">
                      {formData.requires_invoice
                        ? "Se aplicará IVA del 16% al total"
                        : "No se agregará IVA al contrato"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.requires_invoice}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_invoice: checked }))}
                  data-testid="requires-invoice-switch"
                />
              </div>

              {/* Pricing Summary */}
              <div className={cn(
                "p-4 rounded-lg space-y-2",
                formData.requires_invoice ? "bg-blue-50" : "bg-green-50"
              )}>
                <div className="flex justify-between items-center text-sm">
                  <span>Subtotal</span>
                  <span className="font-medium">
                    {formData.currency === "USD" ? "USD " : ""}${subtotalPrice.toLocaleString("es-MX")}
                  </span>
                </div>

                {formData.requires_invoice && (
                  <div className="flex justify-between items-center text-sm text-blue-700">
                    <span>IVA (16%)</span>
                    <span className="font-medium">
                      {formData.currency === "USD" ? "USD " : ""}${vatAmount.toLocaleString("es-MX")}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-lg font-medium">Total</span>
                  <span className={cn(
                    "text-2xl font-bold",
                    formData.requires_invoice ? "text-blue-600" : "text-green-600"
                  )}>
                    {formData.currency === "USD" ? "USD " : ""}${totalPrice.toLocaleString("es-MX")}
                  </span>
                </div>

                {formData.currency === "USD" && formData.exchange_rate && (
                  <div className="pt-2 border-t border-slate-200 text-slate-600">
                    <div className="flex justify-between items-center text-sm">
                      <span>Equivalente en MXN</span>
                      <span className="font-medium">${totalMXN.toLocaleString("es-MX")} MXN</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">TC: {formData.exchange_rate} MXN/USD</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sales Owner (Role-based Logic - P0) */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <User className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-lg text-slate-800">
                  Vendedor Asignado {(vendorSelectionRequired || isAutoAssignRole) && <span className="text-red-500">*</span>}
                </h3>
                {/* Role-based badges */}
                {isVendedorOnly && formData.sales_owner_id === user?.id && (
                  <Badge className="bg-green-100 text-green-800 text-xs">Auto-asignado (editable)</Badge>
                )}
                {isVendedorLogistica && !formData.sales_owner_id && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">Selección obligatoria</Badge>
                )}
                {vendorSelectionRequired && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">Obligatorio</Badge>
                )}
              </div>
              
              {/* Contextual help messages */}
              {vendorSelectionRequired && !formData.sales_owner_id && (
                <p className="text-sm text-amber-600">
                  Como {hasAdminPermissions ? "administrador" : "logística"}, debe seleccionar un vendedor
                </p>
              )}
              {isVendedorLogistica && !formData.sales_owner_id && (
                <p className="text-sm text-amber-600">
                  Seleccione el vendedor asignado a este contrato
                </p>
              )}
              {isVendedorOnly && (
                <p className="text-sm text-green-600">
                  Está asignado a usted por defecto. Puede cambiarlo a otro vendedor (se pedirá justificación).
                </p>
              )}
              
              <Select
                value={formData.sales_owner_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, sales_owner_id: value }))}
                disabled={!canChangeVendor}
              >
                <SelectTrigger 
                  data-testid="sales-owner-select" 
                  className={cn(
                    !formData.sales_owner_id && vendorSelectionRequired ? "border-amber-400" : "",
                    !canChangeVendor ? "bg-slate-100 cursor-not-allowed" : ""
                  )}
                >
                  <SelectValue placeholder="Seleccionar vendedor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendedores.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} {v.id === user?.id ? "(Yo)" : ""} {v.role === "admin" ? "(Admin)" : v.role === "admin_vendedor" ? "(Admin+V)" : v.role === "vendedor_logistica" ? "(V+L)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* P1-C: Sales Agent selector removed - sales_owner_id is now the single source of truth */}

              {/* Vendedor Logistica reassignment reason - required when changing vendor */}
              {isVLReassigning && (
                <div className="space-y-3 pt-3 border-t border-blue-200 bg-blue-50 p-3 rounded-md">
                  <div className="flex items-center gap-2 text-blue-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Reasignación de vendedor</span>
                    <Badge className="bg-blue-200 text-blue-800 text-xs">Obligatorio</Badge>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Motivo de reasignación *</Label>
                    <Textarea
                      value={formData.vl_reassign_reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, vl_reassign_reason: e.target.value }))}
                      placeholder="Explique por qué reasigna este contrato a otro vendedor..."
                      rows={2}
                      className={cn(
                        "resize-none",
                        !formData.vl_reassign_reason ? "border-blue-400" : ""
                      )}
                      data-testid="vl-reassign-reason"
                      required
                    />
                    <p className="text-xs text-blue-600">
                      Este motivo será registrado en el historial de auditoría para control de comisiones
                    </p>
                  </div>
                </div>
              )}

              {isOnBehalf && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Captura en nombre de otro vendedor</span>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Razón *</Label>
                    <Select
                      value={formData.on_behalf_reason}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, on_behalf_reason: value }))}
                    >
                      <SelectTrigger data-testid="on-behalf-reason-select">
                        <SelectValue placeholder="Seleccionar razón..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ON_BEHALF_REASONS.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Nota adicional (opcional)</Label>
                    <Textarea
                      value={formData.on_behalf_note}
                      onChange={(e) => setFormData(prev => ({ ...prev, on_behalf_note: e.target.value }))}
                      placeholder="Detalles adicionales..."
                      rows={2}
                      data-testid="on-behalf-note"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-lg text-slate-800">Descripción del Servicio / Itinerario</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Este texto aparecerá en el PDF del contrato. Describa el itinerario detallado del servicio.
                </p>
              </div>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Ej: La unidad transportará a los pasajeros desde Puerto Vallarta hacia Bucerías para asistir a una convención empresarial. Posteriormente se realizará traslado a restaurante para cena programada..."
                rows={4}
                data-testid="notes-input"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || checkingAvailability} data-testid="submit-contract-btn">
              {checkingAvailability ? "Verificando disponibilidad..." : (loading ? "Creando..." : "Crear Contrato")}
            </Button>
          </div>
        </form>
      </div>

      {/* v4.61.0 (Paso 2): Pop-up de advertencia de disponibilidad exacta */}
      <AlertDialog open={availabilityDialog.open} onOpenChange={(o) => !o && setAvailabilityDialog({ open: false, info: null })}>
        <AlertDialogContent data-testid="availability-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Disponibilidad de unidades</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  Para estas fechas no hay unidades internas disponibles con la capacidad y tipo solicitados.
                  ¿Deseas continuar?
                </p>
                {availabilityDialog.info?.lines && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1" data-testid="availability-breakdown">
                    {availabilityDialog.info.lines
                      .filter((l) => !l.sufficient)
                      .map((l, i) => (
                        <div key={i} className="flex justify-between gap-4">
                          <span className="font-medium text-slate-700">{l.type} · {l.capacity} pax</span>
                          <span className="text-amber-700">
                            {l.available} de {l.required} disponibles · faltan {l.shortfall}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Si continúas, el contrato se creará y las líneas sin unidad interna quedarán marcadas como
                  <span className="font-medium"> requiere subarrendamiento</span> para Logística. No se asignará una unidad falsa.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="availability-cancel-btn">Cancelar / Volver</AlertDialogCancel>
            <AlertDialogAction onClick={handleAvailabilityContinue} data-testid="availability-continue-btn">
              Continuar de todas formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
